import React, { useEffect } from 'react';
import { ApaVisualsSoccerComponent } from '@src/interfaces';
import { trackEvent } from '@src/utils/et';
import { loadScript } from '@src/utils/scripts';
import BrandedTitle from '@src/components/Branding/BrandedTitle';
import { useInView } from 'react-intersection-observer';
import {Models} from "@data/et-web-api";
import styles from './Soccer.module.scss';

interface ApaSpine {
  setup: {
    init: {
      new (): {
        start: () => void;
      };
    };
  };
}

function baseViewProps(view: string) {
  switch (view) {
    // Kalender mit Filter
    case 'calendar.s1':
      return {
        'data-package': 'soccer.event',
        'data-view': 'page:calendar',
        'data-calendar-hasfilter': 'true',
        'data-calendar-size': '18',
      };

    // Kalender ohne Filter
    case 'calendar.s2':
      return {
        'data-package': 'soccer.event',
        'data-view': 'page:calendar',
        'data-calendar-hasfilter': 'false',
        'data-calendar-size': '18',
      };

    // Live
    case 'live.s1':
      return {
        'data-package': 'soccer.live',
        'data-view': 'page:live',
        'data-live-hasnav': 'false',
      };

    // Live mit Highlights
    case 'live.s2':
      return {
        'data-package': 'soccer.live',
        'data-view': 'page:live',
        'data-live-content': 'highlight',
        'data-live-hasnav': 'false',
      };

    // Live mit Navigation
    case 'live.s3':
      return {
        'data-package': 'soccer.live',
        'data-view': 'page:live',
      };

    // Next
    case 'next.s1':
      return {
        'data-package': 'soccer.event',
        'data-view': 'page:calendar-filter:next',
        'data-calendar-hasfilter': 'false',
        'data-calendar-size': '18',
      };

    // Next / only 1
    case 'next.s2':
      return {
        'data-package': 'soccer.event',
        'data-view': 'page:calendar-filter:next',
        'data-calendar-hasfilter': 'false',
        'data-calendar-size': '1',
      };

    // Ergebnisse ohne Filter
    case 'results.s1':
      return {
        'data-package': 'soccer.event',
        'data-view': 'page:results',
        'data-results-hasfilter': 'false',
      };

    // Ergebnisse mit Filter
    case 'results.s2':
      return {
        'data-package': 'soccer.event',
        'data-view': 'page:results',
      };

    // Tabelle ohne Navigation
    case 'standings.s1':
      return {
        'data-package': 'soccer.standings',
        'data-view': 'page:standings',
        'data-standings-hasnav': 'false',
      };

    // Tabelle mit Navigation
    case 'standings.s2':
      return {
        'data-package': 'soccer.standings',
        'data-view': 'page:standings',
        'data-standings-hasnav': 'true',
      };

    // Team Details
    case 'team.s1':
      return {
        'data-package': 'soccer.team',
        'data-view': 'page:team.details-team:1438',
        'data-team-section': 'actual',
        'data-team-hasnav': 'true',
      };

    // Teams
    case 'team.s2':
      return {
        'data-package': 'soccer.team',
        'data-view': 'page:team',
      };

    default:
      console.warn('Unknown soccer view', view);
      return {};
  }
}

function getViewProps(view: string, dataView?: string) {
  const viewProps = baseViewProps(view);

  if (dataView) {
    return {
      ...viewProps,
      ...{
        'data-view': dataView,
      },
    };
  }

  return viewProps;
}

const Soccer = ({ id, title, link, branding, view, dataView }: ApaVisualsSoccerComponent) => {
  const [ref, inView] = useInView();
  useEffect(() => {
    loadScript<ApaSpine>(
      'https://static.puls24.at/visuals/soccer/spine/apa.spine.3.0.min.js',
      'apa',
    ).then(apa => {
      // eslint-disable-next-line new-cap
      new apa.setup.init().start();
    });
  }, [id]);

  useEffect(() => {
    if (inView) {
      const eventName: Models.BannerImpression['eventName'] = 'Banner Impression';
      const eventPayload: Models.BannerImpression['eventPayload'] = {
        bannerName: `APA Widget + ${dataView}`,
      }
      trackEvent(eventName, eventPayload);
    }
  }, [inView, dataView]);

  const containerId = `soccer${id}`;
  const viewProps = getViewProps(view, dataView);

  return (
    <div className={styles.soccerContainer}>
      <BrandedTitle branding={branding} link={link} title={title} />

      <div
        ref={ref}
        className={`apaOuterFrame ${styles.apaOuterFrame}`}
        id={containerId}
        {...viewProps}
      />
    </div>
  );
};

export default Soccer;
